import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ChevronUp, CheckCircle2, Circle, ExternalLink } from "lucide-react";
import { SubscribeMessagesVisual, RegisterPhonePinVisual } from "./WhatsAppWizardVisuals";
import { api } from "@/lib/api";

const outcomeColor = (o) => {
  if (o === "message_received") return "bg-[#DCFCE7] text-[#166534]";
  if (o === "status_only") return "bg-[#DBEAFE] text-[#1E40AF]";
  if (o === "unknown_phone_id" || o === "bad_signature") return "bg-[#FEE2E2] text-[#991B1B]";
  return "bg-[#F4F4F5] text-[#52525B]";
};

/**
 * Persistent training guide at the top of the WhatsApp Inbox.
 * Shows the 6 tasks Meta requires beyond wizard Step 4.
 * Always visible — can be collapsed but never permanently hidden, so it
 * stays as a reference the user can revisit any time.
 *
 * Checkbox state persists in localStorage per workspace so it doesn't
 * reset across sessions.
 */
export default function WhatsAppPostSetupChecklist({ workspaceId, account, configured, onOpenWizard }) {
  const storageKey = `wa_postsetup_${workspaceId || "default"}`;
  const [state, setState] = useState({ done: {}, collapsed: false, expanded: null });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setState((s) => ({ ...s, ...JSON.parse(raw) }));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const persist = (next) => {
    setState(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
  };
  const toggleDone = (id) => persist({ ...state, done: { ...state.done, [id]: !state.done[id] } });
  const toggleExpand = (id) => persist({ ...state, expanded: state.expanded === id ? null : id });
  const toggleCollapse = () => persist({ ...state, collapsed: !state.collapsed });

  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const loadActivity = async () => {
    if (activityLoading) return;
    setActivityLoading(true);
    try {
      const r = await api.get("/whatsapp/webhook-activity");
      setActivity(r.data || []);
    } catch (e) {
      setActivity([]);
    } finally { setActivityLoading(false); }
  };

  const [subState, setSubState] = useState(null); // null | {subscribed, apps} | {error}
  const [subBusy, setSubBusy] = useState(false);
  const [phoneState, setPhoneState] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const verifyPhone = async () => {
    if (phoneBusy) return;
    setPhoneBusy(true);
    try {
      const r = await api.get("/whatsapp/phone-check");
      setPhoneState({ ok: true, ...r.data });
    } catch (e) {
      setPhoneState({ ok: false, error: e?.response?.data?.detail || "Verify failed" });
    } finally { setPhoneBusy(false); }
  };
  const checkSubscription = async () => {
    if (subBusy) return;
    setSubBusy(true);
    try {
      const r = await api.get("/whatsapp/subscription-check");
      setSubState({ subscribed: r.data.subscribed, apps: r.data.apps });
    } catch (e) {
      setSubState({ error: e?.response?.data?.detail || "Check failed" });
    } finally { setSubBusy(false); }
  };
  const subscribeNow = async () => {
    if (subBusy) return;
    setSubBusy(true);
    try {
      await api.post("/whatsapp/subscribe-waba");
      const r = await api.get("/whatsapp/subscription-check");
      setSubState({ subscribed: r.data.subscribed, apps: r.data.apps, justSubscribed: true });
    } catch (e) {
      setSubState({ error: e?.response?.data?.detail || "Subscribe failed" });
    } finally { setSubBusy(false); }
  };

  const items = [
    {
      id: "subscribe-waba",
      title: "Subscribe your WhatsApp Business Account to the App",
      required: true,
      short: "The #1 hidden Meta gotcha. App-level webhook + messages field are NOT enough — the WABA has to be linked to the App too.",
      body: (
        <>
          <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-sm p-2.5 text-[11px] text-[#991B1B]">
            <div className="font-bold mb-1">If your inbox stays empty even after subscribing to <span className="font-mono">messages</span> — this is almost certainly the fix.</div>
            Meta has <b>two</b> subscription layers: (a) App-level webhook fields (Task 2), and
            (b) linking your <b>WhatsApp Business Account (WABA) → this App</b>. Missing (b) is why
            Meta shows the webhook as Active but never actually POSTs your incoming messages.
          </div>

          {account?.phone_number_id && (
            <div className="mt-2 bg-[#EFF6FF] border border-[#BFDBFE] rounded-sm p-2.5 text-[11px] text-[#1E3A8A]">
              <div className="font-bold mb-1">⚠ Cross-check against Meta before subscribing</div>
              <div>CollectOne currently uses:</div>
              <div className="mt-1 space-y-0.5 font-mono text-[10px] text-[#0A0A0B]">
                <div>Phone Number ID: <b>{account.phone_number_id}</b></div>
                <div>WhatsApp Business Account ID: <b>{account.business_account_id || "(missing)"}</b></div>
                {account.display_phone_number && <div>Display number: <b>{account.display_phone_number}</b></div>}
              </div>
              <div className="mt-2 text-[10px] text-[#1E40AF]">
                Open Meta → <b>WhatsApp → API Setup</b> and confirm the number showing above matches the
                <b> registered</b> phone row on Meta — <b>not</b> a "Not registered" duplicate. If Meta shows
                multiple entries for the same phone number, only the row marked <b>Registered</b> with
                <b> Subscribe webhooks ON</b> can receive incoming messages. If IDs don't match, click
                Setup at the top → Step 2 → paste the correct Phone Number ID + WABA ID, save, then
                come back here and click Subscribe now.
              </div>
            </div>
          )}

          <p className="mt-2">
            Once IDs match, click below. CollectOne calls Meta's{" "}
            <span className="font-mono">POST /{"{"}WABA_ID{"}"}/subscribed_apps</span> using your saved
            access token.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={verifyPhone}
              disabled={phoneBusy}
              data-testid="wa-verify-phone-btn"
              className="text-[11px] font-semibold px-2.5 py-1 bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-60"
            >
              {phoneBusy ? "Asking Meta…" : "Verify credentials with Meta"}
            </button>
            <button
              onClick={subscribeNow}
              disabled={subBusy}
              data-testid="wa-subscribe-waba-btn"
              className="text-[11px] font-semibold px-2.5 py-1 bg-[#4338CA] text-white rounded-sm hover:bg-[#3730A3] disabled:opacity-60"
            >
              {subBusy ? "Working…" : "Subscribe now"}
            </button>
            <button
              onClick={checkSubscription}
              disabled={subBusy}
              data-testid="wa-check-subscription-btn"
              className="text-[11px] font-semibold px-2.5 py-1 border co-hairline rounded-sm hover:bg-[#F4F4F5] disabled:opacity-60"
            >
              Check current status
            </button>
          </div>
          {phoneState && (
            <div className={`mt-2 text-[11px] p-2.5 rounded-sm border ${
              !phoneState.ok
                ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B]"
                : "bg-[#DCFCE7] border-[#BBF7D0] text-[#166534]"
            }`}>
              {!phoneState.ok && (
                <div>
                  <div className="font-bold">✗ Meta rejected this Phone Number ID.</div>
                  <div className="mt-0.5 text-[10px] font-mono break-all">{phoneState.error}</div>
                  <div className="mt-1 text-[10px]">
                    Most likely: the Phone Number ID stored on CollectOne doesn't exist, is on a
                    different WABA, or the access token doesn't have permission. Click <b>Setup</b> at
                    the top, revisit Step 2, and paste the IDs from the <b>Registered</b> row on Meta.
                  </div>
                </div>
              )}
              {phoneState.ok && (
                <div>
                  <div className="font-bold">
                    ✓ Meta confirms Phone Number ID <span className="font-mono">{phoneState.stored?.phone_number_id}</span>
                  </div>
                  <div className="mt-1 space-y-0.5 text-[10px] text-[#0A0A0B]">
                    {phoneState.phone?.display_phone_number && (
                      <div>Display: <b>{phoneState.phone.display_phone_number}</b></div>
                    )}
                    {phoneState.phone?.verified_name && (
                      <div>Name: <b>{phoneState.phone.verified_name}</b></div>
                    )}
                    {phoneState.phone?.quality_rating && (
                      <div>Quality: <b>{phoneState.phone.quality_rating}</b></div>
                    )}
                    {phoneState.phone?.code_verification_status && (
                      <div>Verified: <b>{phoneState.phone.code_verification_status}</b></div>
                    )}
                    {phoneState.phone?.messaging_limit_tier && (
                      <div>Tier: <b>{phoneState.phone.messaging_limit_tier}</b></div>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-[#166534]">
                    Compare the display number above with Meta → API Setup. If it doesn't match your
                    business number, you pasted the wrong Phone Number ID.
                  </div>
                </div>
              )}
            </div>
          )}
          {subState && (
            <div className={`mt-2 text-[11px] p-2.5 rounded-sm border ${
              subState.error
                ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B]"
                : subState.subscribed
                  ? "bg-[#DCFCE7] border-[#BBF7D0] text-[#166534]"
                  : "bg-[#FEF3E7] border-[#FDE68A] text-[#78350F]"
            }`}>
              {subState.error && <span>✗ {subState.error}</span>}
              {!subState.error && subState.subscribed && (
                <span>
                  ✓ {subState.justSubscribed ? "Just subscribed!" : "Already subscribed."} Meta will now POST incoming messages to CollectOne.
                  {subState.apps?.length > 0 && (
                    <span className="block mt-1 text-[#3F3F46] text-[10px]">
                      Subscribed apps: {subState.apps.map((a) => a.name || a.id).join(", ")}
                    </span>
                  )}
                </span>
              )}
              {!subState.error && !subState.subscribed && (
                <span>⚠ No apps subscribed to your WABA yet. Click <b>Subscribe now</b> above.</span>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      id: "subscribe-messages",
      title: 'Subscribe to the "messages" webhook field',
      required: true,
      short: "Without this, client replies won't reach CollectOne — even though the webhook is validated.",
      body: (
        <>
          <SubscribeMessagesVisual />
          <p className="mt-2">
            On Meta's <b>WhatsApp → Configuration</b> page, scroll to <b>Webhook fields</b>. Click{" "}
            <b>Manage</b> next to <b>messages</b> and toggle it ON. Optionally also subscribe to{" "}
            <b>message_status</b> to get delivered / read ticks in the inbox.
          </p>
        </>
      ),
      link: "https://developers.facebook.com/apps/",
    },
    {
      id: "register-pin",
      title: "Register your phone number with a 6-digit PIN",
      required: true,
      short: "Meta requires the number to be registered on Cloud API before you can send messages from it.",
      body: (
        <>
          <RegisterPhonePinVisual />
          <p className="mt-2">
            Go to <b>WhatsApp → API Setup</b>. Next to your business phone number, click{" "}
            <b>Manage phone number</b> → <b>Two-step verification</b>. Set any 6-digit PIN, click{" "}
            <b>Register</b>. Save the PIN — Meta may ask again if the number is ever re-registered.
          </p>
        </>
      ),
      link: "https://developers.facebook.com/apps/",
    },
    {
      id: "test-message",
      title: "Send a test message from your personal WhatsApp",
      required: true,
      short: "Message flows only inbound (personal → business). Outbound tests from Meta won't appear here.",
      body: (
        <>
          <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-sm p-2.5 text-[11px] text-[#991B1B]">
            <div className="font-bold mb-1">Direction matters — the #1 reason messages don't appear</div>
            Send FROM your <b>personal</b> WhatsApp TO your <b>business API number</b>. Sending the
            other way round (using Meta's "Send Message" test tool from API Setup) does <b>not</b>
            create an inbox entry — it's outbound, not incoming.
          </div>
          <p className="mt-2">
            Open WhatsApp on your personal phone, add your business display number as a new contact,
            send any message (e.g. <i>"hi"</i>). Within <b>~10 seconds</b> it should appear in the
            Inbox list on the left of this screen.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={loadActivity}
              disabled={activityLoading}
              data-testid="wa-view-webhook-activity"
              className="text-[11px] font-semibold px-2.5 py-1 border co-hairline rounded-sm hover:bg-[#F4F4F5] disabled:opacity-60"
            >
              {activityLoading ? "Checking…" : "Check what Meta actually sent"}
            </button>
          </div>
          {activity && (
            <div className="mt-2 border co-hairline rounded-sm bg-white">
              <div className="px-3 py-2 border-b co-hairline text-[10px] uppercase tracking-wider text-[#71717A] flex items-center justify-between">
                <span>Last {activity.length} webhook events · 7-day retention</span>
                <button onClick={() => setActivity(null)} className="text-[#4338CA] font-semibold">Hide</button>
              </div>
              {(() => {
                const orphans = (activity || []).filter((a) => a.outcome === "unknown_phone_id" && a.detail?.phone_id);
                const orphanIds = Array.from(new Set(orphans.map((o) => o.detail.phone_id)));
                if (orphanIds.length === 0) return null;
                return (
                  <div className="px-3 py-2 bg-[#FEE2E2] border-b border-[#FCA5A5] text-[11px] text-[#991B1B]">
                    <div className="font-bold">⚠ Mismatch detected — this is why your inbox is empty.</div>
                    <div className="mt-1">
                      Meta is delivering messages on your WABA, but with{" "}
                      <b>phone_number_id{orphanIds.length > 1 ? "s" : ""}</b>{" "}
                      {orphanIds.map((id, i) => (
                        <span key={id}>
                          <span className="font-mono bg-white/60 px-1 rounded">{id}</span>
                          {i < orphanIds.length - 1 ? ", " : ""}
                        </span>
                      ))}{" "}
                      that CollectOne isn't listening on. Copy the ID above → click <b>Setup</b> at
                      the top → <b>Step 2</b> → paste into <b>Phone number ID</b> → Save → return
                      here and click <b>Subscribe now</b>.
                    </div>
                  </div>
                );
              })()}
              {activity.length === 0 ? (
                <div className="px-3 py-4 text-[11px] text-[#DC2626]">
                  Nothing received yet. That means Meta isn't POSTing to CollectOne — check that the
                  <b> messages</b> field is subscribed (Task 1) and that your webhook shows "Active"
                  on Meta's Configuration page.
                </div>
              ) : (
                <ul className="divide-y co-hairline max-h-52 overflow-y-auto">
                  {activity.map((a, i) => (
                    <li key={i} className="px-3 py-2 text-[11px] flex items-start gap-2">
                      <span className={`shrink-0 px-1.5 py-0.5 rounded-sm font-mono text-[9px] font-bold uppercase ${outcomeColor(a.outcome)}`}>
                        {a.outcome}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[#3F3F46]">
                          {(a.detail?.messages_count ?? 0) > 0 && `${a.detail.messages_count} message(s) `}
                          {(a.detail?.statuses_count ?? 0) > 0 && `${a.detail.statuses_count} status(es) `}
                          {a.detail?.field && <span className="text-[#71717A]">· field: <span className="font-mono">{a.detail.field}</span></span>}
                        </div>
                        <div className="text-[10px] text-[#71717A]">
                          {new Date(a.ts * 1000).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      ),
    },
    {
      id: "templates",
      title: "Create utility message templates",
      required: true,
      short: "Meta only allows outbound reminders using pre-approved templates. Utility templates approve in ~30 min.",
      body: (
        <p>
          In Meta go to <b>WhatsApp → Message Templates → Create Template</b>. Pick category{" "}
          <b>Utility</b> (not Marketing). Suggested first three templates: <br />
          <span className="font-mono text-[11px]">payment_reminder_due_soon</span>,{" "}
          <span className="font-mono text-[11px]">payment_receipt_confirmation</span>,{" "}
          <span className="font-mono text-[11px]">payment_overdue</span>. <br />
          Add both <b>English</b> and <b>Hindi</b> versions if you have Indian clients. Once approved,
          CollectOne's scheduler fires them at 9 AM IST daily for clients whose due date is 7 / 3 / 0
          days away.
        </p>
      ),
      link: "https://business.facebook.com/wa/manage/message-templates/",
    },
    {
      id: "permanent-token",
      title: "Upgrade to a permanent access token",
      required: true,
      short: "The token you pasted expires in 24 hours — reminders will stop until you refresh it.",
      body: (
        <p>
          Go to{" "}
          <a href="https://business.facebook.com/settings/system-users" target="_blank"
             rel="noopener noreferrer" className="text-[#4338CA] underline">business.facebook.com → Business Settings → System Users</a>{" "}
          → <b>Add</b> → give it a name (e.g. <i>"CollectOne API"</i>). Assign the WhatsApp app + your
          WABA with <b>Full control</b>. Click <b>Generate new token</b>, pick your app, set{" "}
          <b>Never expires</b>, and tick <b>whatsapp_business_messaging</b> +{" "}
          <b>whatsapp_business_management</b>. Copy the new token → return to CollectOne wizard{" "}
          <button onClick={onOpenWizard} className="text-[#4338CA] underline font-semibold">Step 2</button>{" "}
          → paste it in the Access Token field → Save.
        </p>
      ),
      link: "https://business.facebook.com/settings/system-users",
    },
    {
      id: "business-verification",
      title: "Complete Business Verification (only when scaling beyond ~250 clients/day)",
      required: false,
      short: "Optional today. Lifts Meta's sandbox cap of 250 conversations per day.",
      body: (
        <p>
          In Meta go to <b>Business Settings → Security Center → Start Verification</b>. Upload your
          GST certificate + Certificate of Incorporation (or Udyam / MSME registration). Approval
          takes 2–5 business days. Once verified, your daily conversation cap is lifted and you can
          also apply for the green tick.
        </p>
      ),
      link: "https://business.facebook.com/settings/security",
    },
  ];

  const doneCount = items.filter((it) => state.done[it.id]).length;
  const total = items.length;
  const allDone = doneCount === total;
  const preSetup = !configured;

  return (
    <div className="border-b co-hairline bg-[#FEFCE8]">
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="co-label text-[#854D0E]">
            {preSetup ? "Training guide · after wizard" : "Finish WhatsApp setup"}
          </div>
          <div className="text-sm font-heading font-bold text-[#0A0A0B] truncate">
            {preSetup
              ? "6 Meta tasks to complete WhatsApp go-live"
              : allDone
                ? "All tasks complete 🎉"
                : `${doneCount}/${total} done — a few more Meta tasks`}
          </div>
        </div>
        <button
          onClick={toggleCollapse}
          data-testid="wa-postsetup-collapse"
          className="p-1 hover:bg-[#FEF3C7] rounded-sm shrink-0"
          title={state.collapsed ? "Show checklist" : "Collapse checklist"}
        >
          {state.collapsed
            ? <ChevronDown className="h-4 w-4 text-[#854D0E]" strokeWidth={1.75} />
            : <ChevronUp className="h-4 w-4 text-[#854D0E]" strokeWidth={1.75} />}
        </button>
      </div>

      {!state.collapsed && (
      <div className="pb-2">
        {items.map((it, idx) => {
          const done = !!state.done[it.id];
          const expanded = state.expanded === it.id;
          return (
            <div key={it.id} className="border-t border-[#FDE68A]">
              <div className="flex items-start gap-2 px-4 py-2">
                <button
                  onClick={() => toggleDone(it.id)}
                  data-testid={`wa-postsetup-check-${it.id}`}
                  className="mt-0.5 shrink-0"
                  title={done ? "Mark not done" : "Mark done"}
                >
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 text-[#15803D]" strokeWidth={2} />
                  ) : (
                    <Circle className="h-4 w-4 text-[#A16207]" strokeWidth={1.75} />
                  )}
                </button>
                <button
                  onClick={() => toggleExpand(it.id)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-[#A16207] font-mono">{String(idx + 1).padStart(2, "0")}</span>
                    <span className={`text-xs font-semibold ${done ? "line-through text-[#71717A]" : "text-[#0A0A0B]"}`}>
                      {it.title}
                    </span>
                    {it.required && !done && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-[#DC2626] bg-[#FEE2E2] px-1 py-0.5 rounded-sm">required</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#52525B] mt-0.5 truncate">{it.short}</div>
                </button>
                <button
                  onClick={() => toggleExpand(it.id)}
                  className="p-0.5 hover:bg-[#FEF3C7] rounded-sm shrink-0"
                  data-testid={`wa-postsetup-expand-${it.id}`}
                >
                  {expanded
                    ? <ChevronDown className="h-4 w-4 text-[#854D0E]" strokeWidth={1.75} />
                    : <ChevronRight className="h-4 w-4 text-[#854D0E]" strokeWidth={1.75} />}
                </button>
              </div>
              {expanded && (
                <div className="px-4 pb-3 pl-11 text-xs text-[#3F3F46] space-y-2">
                  {it.body}
                  {it.link && (
                    <a
                      href={it.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#4338CA] font-semibold text-[11px] hover:text-[#3730A3]"
                    >
                      Open on Meta <ExternalLink className="h-3 w-3" strokeWidth={2} />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
